import { nanoid } from 'nanoid';
import type { AnswerTrackerData, CommonTrackerData, PixelEvent as PixelEventName, SomeTrackerData } from 'schema';
import { ExpressionsParser } from './expressions';
import { PixelManager } from './pixel';
import type { AuthProvider, Event, EventIdentity, PixelEvent, QuestionType, TrackingConstructorOptions } from './types';
import { allSettled, isObject, isString, noop, notNull } from './utils';

const NULL_FILLER = '__NULL__';
const DIGIT_REGEX = /\d+/;
const STRING_REGEX = /(["'])(?:(?=(\\?))\2.)*?\1/;
const PLACEHOLDER_REGEX = /\${(GET:)?[\w-]+}/g;
const KNOWN_SOURCES = ['polls', 'polls_cxm', 'polls_widget', 'polls_invite'] as const;
const DEFAULT_SOURCE = KNOWN_SOURCES[0];

export class TrackingError extends Error {
  public readonly id;

  constructor(id: string, message: string) {
    super(message);
    this.id = id;
  }
}

export class Tracking {
  public isStarted = false;
  public isFinished = false;
  public readonly commonHeaders: HeadersInit;
  public readonly commonData: CommonTrackerData;

  private user?: { id: string };
  private userToken?: string;
  private authProvider: AuthProvider | null;
  private readonly eventMap: Partial<Record<PixelEventName, PixelEvent[]>> = {};
  private readonly getParams: URLSearchParams | undefined;

  public updatePollsIc(newPollsIc: string) {
    this.commonData.polls_ic = newPollsIc;
  }

  public setAuthProvider(authProvider: AuthProvider | null) {
    this.authProvider = authProvider;
  }

  constructor({
    id,
    env,
    wave,
    revision,
    referer,
    source,
    pixels,
    getParams,
    pollsIc,
    authProvider,
    questionCount,
    pixelVersion,
  }: TrackingConstructorOptions) {
    this.getParams = getParams;
    this.authProvider = authProvider;

    const resolvedSource = resolveSource(source, getParams);

    this.commonData = {
      quiz_id: id,
      polls_ic: pollsIc,
      revision: revision?.toString(),
      wave,
      questions_count: questionCount,
      source: env === 'prod' ? resolvedSource : resolvedSource + '_test',
      version: pixelVersion,
      referer,
      ...this.getUserData(this.user),
    };
    this.commonHeaders = this.getCommonHeaders(getParams);

    for (const pixel of pixels) {
      if (pixel.data.events) {
        for (const event of pixel.data.events) {
          if (pixel.type === 'javascript') continue;
          if (!this.eventMap[event.name]) this.eventMap[event.name] = [];

          const eventBase = {
            id: nanoid(10),
            when: event.when ?? undefined,
            isSystem: !!pixel.isSystem,
            pixelId: pixel.id,
          };

          const domain = resolvePixelDomain(pixel);
          const url = makeUrlString(domain, event.pathname, event.params);

          this.eventMap[event.name]?.push({
            ...eventBase,
            type: pixel.type,
            method: event.method!,
            url,
            data: event.data,
            contentType: event.contentType ?? 'application/x-www-form-urlencoded',
            timeout: event.timeout,
          });
        }
      }
    }
  }

  private getCommonHeaders(getParams: URLSearchParams | undefined): HeadersInit {
    const headers: HeadersInit = {};

    const bundleVersion = process.env.__APP_VERSION__;
    if (bundleVersion) headers['x-components-version'] = bundleVersion;

    const commonHeaders = ['x-app-name', 'x-platform', 'x-app-version', 'x-sdk-version'];
    for (const header of commonHeaders) {
      const headerValue = getParams?.get(header);
      if (headerValue) headers[header] = headerValue;
    }

    return headers;
  }

  public setUser(user: { id: string; token: string } | null) {
    if (user) {
      this.userToken = user.token;
      this.user = user;
      Object.assign(this.commonData, this.getUserData(user));
    }
  }

  public load() {
    this.trackAndForget('load');
  }

  public init() {
    if (!this.isStarted) this.start();
  }

  public start() {
    this.isStarted = true;
    this.trackAndForget('start');
  }

  public async answer(
    {
      setId,
      deckId,
      groupId,
      questionId,
      questionText,
      questionType,
      questionIndex,
      answers,
      answersIds,
      answersOrder,
      answeredIds,
      answeredOthers,
      awardAmount,
      elapsedTime,
      isTimeExpired,
      previousHash,
      currentHash,
      transform,
    }: {
      setId: string | undefined;
      deckId: string | undefined;
      groupId: string | undefined;
      questionId: string;
      questionText: string;
      questionType: string;
      questionIndex: number;
      answers: { id: string; text?: string }[];
      answersIds: string[];
      answersOrder?: number[];
      answeredIds: string[];
      answeredOthers?: string;
      awardAmount?: number;
      elapsedTime?: number | null;
      isTimeExpired?: boolean;
      currentHash: string | null;
      previousHash: string | null;
      transform?: (data: AnswerTrackerData) => AnswerTrackerData;
    },
    retryIds?: string[],
  ) {
    const data = this.answerData({
      setId,
      deckId,
      groupId,
      questionId,
      questionText,
      questionType,
      questionIndex,
      answers,
      answersIds,
      answersOrder,
      answeredIds,
      answeredOthers,
      awardAmount,
      elapsedTime,
      isTimeExpired,
      currentHash,
      previousHash,
      transform,
    });
    return this.track('answer', data, retryIds);
  }

  public finish(
    {
      finalId,
      finalIndex,
      points,
      isTimeExpired,
      correctAnswersCount,
      currentHash,
      previousHash,
    }: {
      finalId: string;
      finalIndex: number;
      points: number;
      correctAnswersCount: number;
      currentHash: string;
      isTimeExpired: boolean;
      previousHash: string | null;
    },
    retryIds?: string[],
  ) {
    this.isFinished = true;
    return this.track(
      'finish',
      {
        ...this.commonData,
        final_id: finalId,
        final_index: finalIndex + 1,
        collected_score: points,
        current_hash: currentHash,
        previous_hash: previousHash,
        correct_answers_count: correctAnswersCount,
        time_expired: isTimeExpired === true ? 1 : 0,
      },
      retryIds,
    );
  }

  public upload({ fileName, path }: { fileName: string; path: string | null; questionId: string }) {
    const data = {
      ...this.commonData,
      file_name: fileName,
      file_path: path,
      file_is_uploaded: path ? 1 : 0,
    };

    this.trackAndForget('upload', data);
  }

  public welcomeClick() {
    this.trackAndForget('welcome-click');
  }

  public finalClick({ finalId, finalIndex }: { finalId: string; finalIndex: number }) {
    this.trackAndForget('final-click', { ...this.commonData, final_id: finalId, final_index: finalIndex + 1 });
  }

  public linkClick({ external_url }: { external_url: string }) {
    this.trackAndForget('quiz-link', {
      ...this.commonData,
      external_url,
    });
  }

  public overall({
    answers_overall,
    questions_count,
    final_id,
    final_number,
    correct_answers_number,
    collected_score,
  }: {
    answers_overall: unknown[];
    questions_count: number;
    final_id: string;
    final_number: string;
    correct_answers_number: number;
    collected_score: number;
  }) {
    return this.track('overall', {
      ...this.commonData,
      answers_overall,
      questions_count,
      final_id,
      final_number,
      correct_answers_number,
      collected_score,
    });
  }

  public trackMediaProgress({
    media_progress,
    questionId,
    questionText,
    questionType,
    questionIndex,
    answerIds,
  }: {
    questionId: string;
    questionText: string;
    questionType: QuestionType;
    questionIndex: number;
    media_progress: string;
    answerIds?: string[];
  }) {
    this.trackAndForget('media-progress', {
      ...this.commonData,
      question_index: questionIndex,
      question_id: questionId,
      question_text: questionText,
      question_type: questionType,
      answers_ids: answerIds?.join(','),
      media_progress,
    });
  }

  private answerData({
    setId,
    deckId,
    groupId,
    questionId,
    questionIndex,
    questionText,
    questionType,
    answers,
    answersIds,
    answersOrder = [],
    answeredIds,
    answeredOthers,
    awardAmount,
    elapsedTime,
    isTimeExpired,
    currentHash,
    previousHash,
    transform,
  }: {
    deckId: string | undefined;
    groupId: string | undefined;
    setId: string | undefined;
    questionId: string;
    questionIndex: number;
    questionText: string;
    questionType: string;
    answers: { id: string; text?: string }[];
    answersIds: string[];
    answersOrder?: number[];
    answeredIds: string[];
    answeredOthers?: string;
    awardAmount?: number;
    elapsedTime?: number | null;
    isTimeExpired?: boolean;
    currentHash: string | null;
    previousHash: string | null;
    transform?: (data: AnswerTrackerData) => AnswerTrackerData;
  }): AnswerTrackerData {
    if (answersIds.length > 0) answers = answers.filter(it => answersIds.includes(it.id));
    if (answersOrder.length > 0) answers = answersOrder.map(index => answers[index]).filter(notNull);

    const data: AnswerTrackerData = {
      ...this.commonData,
      set_id: setId || null,
      group_id: groupId ?? null,
      question_deck_id: deckId ?? null,
      question_index: questionIndex,
      question_id: questionId,
      question_text: questionText,
      question_type: questionType,
      elapsed_time: elapsedTime,
      answered_ids: answeredIds,
      time_expired: isTimeExpired === true ? 1 : 0,
      answered_indexes: answeredIds.map(id => {
        const foundIndex = answers.findIndex(answer => answer.id === id);
        return foundIndex !== -1 ? foundIndex : answers.length;
      }),
      answered_others: answeredOthers ?? null,
      answers: answers.map(answer => `${answer.id}:"${escapeAnswerText(answer.text)}"`),
      collected_score: awardAmount,
      current_hash: currentHash,
      previous_hash: previousHash,
    };

    return transform?.(data) ?? data;
  }

  private trackAndForget(eventName: PixelEventName, eventData: SomeTrackerData = this.commonData) {
    const { systemPromises, customPromises } = this.track(eventName, eventData);
    Promise.all([systemPromises, customPromises]).catch(noop);
  }

  private track(eventName: PixelEventName, eventData: SomeTrackerData = this.commonData, retryIds?: string[]) {
    const retry = Array.isArray(retryIds);
    const events = this.eventMap[eventName] || [];

    const sendedEvents: Pick<Event, 'id' | 'isSystem'>[] = [];
    const systemPromises: Promise<EventIdentity>[] = [];
    const customPromises: Promise<EventIdentity>[] = [];
    eventData = { ...eventData, retry: retry ? 1 : 0 };

    for (const event of events) {
      if (event.when && !this.eventConditionResolver(event.when, eventData)) continue;
      if (retry && !retryIds.includes(event.id)) continue;
      if (event.type === 'javascript') continue;

      const { data, method, contentType = 'application/x-www-form-urlencoded' } = event;
      const url = this.replaceUrlEncodedPlaceholders(event.url, eventData);
      const body = data
        ? contentType === 'application/json'
          ? this.replaceJSONPlaceholders(data, eventData)
          : this.replaceUrlEncodedPlaceholders(data, eventData)
        : null;

      const headers = {
        ...this.commonHeaders,
        'Content-Type': contentType,
        ...(this.userToken && { Authorization: `Bearer ${this.userToken}` }),
      };

      const promise = new PixelManager(event.type)
        .track(url, { options: { method, body, headers }, timeout: event.timeout })
        .then(
          () => ({ id: event.id }),
          (err: unknown) => {
            throw new TrackingError(
              event.id,
              !!err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                ? err.message
                : 'unknown',
            );
          },
        );

      sendedEvents.push({ id: event.id, isSystem: event.isSystem });
      if (event.isSystem) systemPromises.push(promise);
      else customPromises.push(promise);
    }

    return { sendedEvents, systemPromises: allSettled(systemPromises), customPromises: allSettled(customPromises) };
  }

  private replaceUrlEncodedPlaceholders(url: string, data: SomeTrackerData) {
    const nextUrl = url.replace(PLACEHOLDER_REGEX, placeholder => {
      const value = this.placeholderResolver(placeholder, data);
      return encodeURIComponent(value === null ? NULL_FILLER : value ?? '');
    });

    return removeNullParams(nextUrl);
  }
  private replaceJSONPlaceholders(jsonString: string, data: SomeTrackerData) {
    try {
      const json = JSON.parse(jsonString) as Record<string, unknown>;
      const DIRECT_COPY_KEYS = ['answers_overall'];

      for (const [key, value] of Object.entries(json)) {
        if (DIRECT_COPY_KEYS.includes(key) && Object.prototype.hasOwnProperty.call(data, key)) {
          json[key] = data[key as keyof SomeTrackerData];
          continue;
        }

        const replacedValue = isString(value)
          ? value.replace(PLACEHOLDER_REGEX, placeholder => {
              const value = this.placeholderResolver(placeholder, data);
              return value === null ? NULL_FILLER : `${value ?? ''}`;
            })
          : value;

        if (replacedValue !== NULL_FILLER) {
          json[key] = replacedValue;
        } else {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete json[key];
        }
      }

      if (isString(json['answered_others'])) {
        try {
          const answered_others_json = JSON.parse(json['answered_others']) as object;

          if (isObject(answered_others_json)) {
            json['answered_others'] = answered_others_json;
          }
        } catch {
          /* empty */
        }
      }

      return JSON.stringify(json);
    } catch {
      return JSON.stringify({});
    }
  }

  private eventConditionResolver(when: string, data: SomeTrackerData) {
    return new ExpressionsParser(when).parse(literal => this.literalResolver(literal, data));
  }

  private literalResolver(literal: string, data: SomeTrackerData) {
    if (!literal || literal === 'null') return null;
    if (DIGIT_REGEX.test(literal)) return +literal;
    if (STRING_REGEX.test(literal)) return literal.slice(1, -1);
    if (PLACEHOLDER_REGEX.test(literal)) return this.placeholderResolver(literal, data);
    if (Object.prototype.hasOwnProperty.call(data, literal))
      return (data as unknown as Record<string, string | number | null | undefined>)[literal];
    return null;
  }

  private placeholderResolver(placeholder: string, data: SomeTrackerData): string | number | null | undefined {
    const { getParams } = this;
    placeholder = placeholder.slice('${'.length, -'}'.length);
    return placeholder.startsWith('GET:')
      ? getParams?.get(placeholder.slice('GET:'.length)) ?? undefined
      : (data as unknown as Record<string, string | number | null | undefined>)[placeholder];
  }

  private getUserData(user: { id: string } | undefined): UserData {
    if (!user) {
      return { user_id: '', twork_id: '' };
    }

    if (this.authProvider === 'twork') {
      return { user_id: null, twork_id: user.id };
    }

    return { user_id: user.id, twork_id: '' };
  }
}

type UserData = Pick<CommonTrackerData, 'user_id' | 'twork_id'>;

function makeUrlString(domain: string, pathname: string | undefined, params: string | null | undefined) {
  const url = new URL(pathname || '', domain);
  const urlString = url.origin + url.pathname;
  return decodeURI(params ? urlString + '?' + params : urlString);
}

function resolvePixelDomain(pixel: { isSystem?: boolean; data: { domain?: string } }) {
  if (
    pixel.isSystem &&
    pixel.data.domain &&
    pixel.data.domain.includes('track.tbank.ru') &&
    window.location.hostname == 'media.t-bank-app.ru'
  ) {
    return pixel.data.domain.replace('track.tbank.ru', 'track.t-bank-app.ru');
  }

  return pixel.data.domain ?? '';
}

function removeNullParams(urlEncoded: string) {
  try {
    const url = new URL(urlEncoded);

    url.pathname = url.pathname
      .split('/')
      .map(it => it.replace(NULL_FILLER, ''))
      .filter(it => it)
      .join('/');

    const urlParams = new URLSearchParams([...url.searchParams]);

    url.search = '?' + new URLSearchParams([...urlParams].filter(([, v]) => v !== NULL_FILLER)).toString();

    return url.toString();
  } catch {
    try {
      const urlParams = new URLSearchParams(urlEncoded);
      return new URLSearchParams([...urlParams].filter(([, v]) => v !== NULL_FILLER)).toString();
    } catch {
      return urlEncoded;
    }
  }
}

function escapeAnswerText(text: string | undefined) {
  const entityMap: Record<string, string> = { '"': '\\"' };
  return typeof text === 'string' ? text.replace(/["]/g, s => entityMap[s]!) : text;
}

function isKnownSource(source: string | null | undefined): source is (typeof KNOWN_SOURCES)[number] {
  return typeof source === 'string' && KNOWN_SOURCES.includes(source as any);
}

function resolveSource(source: string | undefined, getParams: URLSearchParams | undefined) {
  if (source) return source;

  const sourceOverride = getParams?.get('source');
  return isKnownSource(sourceOverride) ? sourceOverride : DEFAULT_SOURCE;
}
